/**
 * 🚀 Live Page Rich Text Fixer 2025
 * Elegante Lösung für HTML-Escape-Probleme auf Live-Seiten
 */

import { cleanRichTextHTML } from '../Pages/utils/richTextUtils';

/**
 * Initialisiert den Rich Text Fixer für Live-Seiten
 * Sollte nach dem Laden der Seite aufgerufen werden
 */
export const initLivePageRichTextFixer = () => {
    // Prüfe ob wir auf einer Live-Seite sind
    const isLivePage = document.querySelector('.page_public') !== null;
    
    if (!isLivePage) {
        console.log('🔧 Rich Text Fixer: Nicht auf Live-Seite, überspringe Initialisierung');
        return;
    }
    
    console.log('🔧 Rich Text Fixer 2025: Initialisiere für Live-Seite (Firestore-kompatibel)');
    
    // ✅ AGGRESSIVE SELEKTOR-STRATEGIE: Finde ALLE Text-Elemente
    const selectors = [
        // Direkte dangerouslySetInnerHTML Elemente
        '.page_public [dangerouslySetInnerHTML]',
        // Widget Container mit Text-Content
        '.page_public .widget-container div',
        '.page_public .widget-boss-wrapper div',
        // Spezifische Rich Text Klassen
        '.page_public .rich-text-output',
        '.page_public .responsive-rich-text',
        // ✅ ALLE TEXT-CONTAINER in Widgets
        '.page_public .widget-container *',
        '.page_public .widget-boss-wrapper *',
        // ✅ ALLE DIVS die Text enthalten könnten
        '.page_public div:not(script):not(style)',
        // ✅ ALLE PARAGRAPHS und SPANS
        '.page_public p',
        '.page_public span'
    ];
    
    let fixedElements = 0;
    let processedElements = new Set(); // Verhindere Doppel-Verarbeitung
    
    selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        
        elements.forEach(element => {
            // Verhindere Doppel-Verarbeitung
            if (processedElements.has(element)) return;
            processedElements.add(element);
            
            const content = element.innerHTML;
            
            // ✅ VERBESSERTE HTML-ESCAPE ERKENNUNG
            if (isDisplayingHtmlAsText(content)) {
                console.log('🔧 Rich Text Fixer: HTML-Escape erkannt, korrigiere:', content.substring(0, 100) + '...');
                
                try {
                    // ✅ MULTI-STEP KORREKTUR
                    const correctedContent = fixEscapedHtml(content);
                    const cleanedContent = cleanRichTextHTML(correctedContent);
                    
                    // Setze korrigierten Content
                    element.innerHTML = cleanedContent;
                    
                    // ✅ STYLING PRESERVATION: Behalte alle bestehenden Styles
                    element.classList.add('rich-text-fixed');
                    
                    // ✅ FONT INHERITANCE FIX: Stelle sicher dass Fonts korrekt vererbt werden
                    const computedStyle = window.getComputedStyle(element);
                    if (computedStyle.fontFamily === 'Arial' || computedStyle.fontFamily.includes('Arial')) {
                        element.style.fontFamily = 'inherit';
                    }
                    
                    fixedElements++;
                    
                } catch (error) {
                    console.warn('⚠️ Rich Text Fixer: Fehler beim Korrigieren von Element:', error);
                }
            }
        });
    });
    
    if (fixedElements > 0) {
        console.log(`✅ Rich Text Fixer 2025: ${fixedElements} Elemente erfolgreich korrigiert`);
        
        // ✅ TRIGGER REFLOW: Stelle sicher dass Browser die Änderungen rendert
        // eslint-disable-next-line no-unused-expressions
        document.body.offsetHeight;
    } else {
        console.log('✅ Rich Text Fixer 2025: Keine Korrekturen nötig - alle Rich Text Elemente korrekt');
    }
};

/**
 * ✅ VERBESSERTE HTML-ESCAPE ERKENNUNG
 * Prüft ob ein Element HTML-Tags als Text anzeigt
 * @param {string} content - Element Content
 * @returns {boolean} - True wenn HTML-Tags als Text angezeigt werden
 */
const isDisplayingHtmlAsText = (content) => {
    if (!content || typeof content !== 'string') return false;
    
    // ✅ ERWEITERTE ESCAPE-PATTERN ERKENNUNG
    const escapePatterns = [
        // HTML-Entities (escaped HTML)
        /&lt;p&gt;/i,
        /&lt;strong&gt;/i,
        /&lt;span/i,
        /&lt;br&gt;/i,
        /&lt;\/p&gt;/i,
        /&lt;\/strong&gt;/i,
        /&lt;\/span&gt;/i,
        
        // ✅ SICHTBARE HTML-TAGS ALS TEXT (das ist dein Problem!)
        /<strong>/i,
        /<\/strong>/i,
        /<b>/i,
        /<\/b>/i,
        /<em>/i,
        /<\/em>/i,
        /<p>/i,
        /<\/p>/i,
        /<span[^>]*>/i,
        /<\/span>/i,
        
        // ✅ FIRESTORE-SPEZIFISCHE PROBLEME
        /&lt;strong&gt;.*?&lt;\/strong&gt;/i,
        /&lt;b&gt;.*?&lt;\/b&gt;/i,
        /&lt;p&gt;.*?&lt;\/p&gt;/i,
        /&lt;span.*?&gt;.*?&lt;\/span&gt;/i,
        
        // Komplexere Patterns
        /^<p[^>]*>.*<\/p>$/s,
        /^<span[^>]*>.*<\/span>$/s,
        /<p[^>]*>.*?<\/p>/s,
        /<span[^>]*style="[^"]*"[^>]*>/i,
        
        // Spezifische Firestore-Escape-Patterns
        /rgba\([^)]+,var\([^)]+\)\)/i, // CSS-Variablen in Style-Attributen
        /<span style="color: rgba\(/i,
        /<strong style="color: rgba\(/i,
        
        // Dein spezifisches Problem-Pattern
        /&amp;&nbsp;/i // &nbsp; als escaped entity
    ];
    
    // ✅ CONTENT-LÄNGE CHECK: Nur längere Texte mit HTML-Struktur prüfen
    if (content.length < 10) return false;
    
    // ✅ PATTERN MATCHING: Prüfe auf alle Escape-Patterns
    const hasEscapedHtml = escapePatterns.some(pattern => pattern.test(content));
    
    // ✅ ZUSÄTZLICHE HEURISTIK: Prüfe auf HTML-Struktur-Indikatoren
    const hasHtmlStructure = (
        content.includes('<p>') || 
        content.includes('<span') || 
        content.includes('<strong>') ||
        content.includes('&lt;') ||
        content.includes('style="')
    ) && (
        content.includes('</p>') || 
        content.includes('</span>') || 
        content.includes('</strong>') ||
        content.includes('&gt;')
    );
    
    return hasEscapedHtml || hasHtmlStructure;
};

/**
 * ✅ MULTI-STEP HTML-ESCAPE KORREKTUR
 * Korrigiert verschiedene Arten von HTML-Escaping
 * @param {string} content - Escaped HTML Content
 * @returns {string} - Korrigierter HTML Content
 */
const fixEscapedHtml = (content) => {
    if (!content || typeof content !== 'string') return content;
    
    let fixed = content;
    
    // ✅ SCHRITT 1: HTML-Entities dekodieren
    fixed = fixed
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' '); // Non-breaking space zu normalem Space
    
    // ✅ SCHRITT 2: Doppelte Escaping korrigieren
    fixed = fixed
        .replace(/&amp;lt;/g, '<')
        .replace(/&amp;gt;/g, '>')
        .replace(/&amp;quot;/g, '"');
    
    // ✅ SCHRITT 3: CSS-Variablen in Style-Attributen korrigieren
    // rgba(39,34,32,var(--O42jJQ,1)) → rgba(39,34,32,1)
    fixed = fixed.replace(
        /rgba\(([^,]+),([^,]+),([^,]+),var\([^,]+,([^)]+)\)\)/g,
        'rgba($1,$2,$3,$4)'
    );
    
    // ✅ SCHRITT 4: Entferne ALLE P-Tags (auch mit Style-Attributen)
    fixed = fixed
        .replace(/<p[^>]*>/g, '') // Öffnende P-Tags mit beliebigen Attributen
        .replace(/<\/p>/g, '<br>'); // Schließende P-Tags zu BR
    
    // ✅ SCHRITT 5: BEHALTE HTML-TAGS aber entferne nur Style-Attribute
    fixed = fixed
        .replace(/<strong[^>]*>/g, '<strong>') // Bereinige Strong-Tags aber behalte sie
        .replace(/<b[^>]*>/g, '<b>') // Bereinige B-Tags aber behalte sie
        .replace(/<em[^>]*>/g, '<em>') // Bereinige Em-Tags aber behalte sie
        .replace(/<i[^>]*>/g, '<i>') // Bereinige I-Tags aber behalte sie
        .replace(/<u[^>]*>/g, '<u>') // Bereinige U-Tags aber behalte sie
        .replace(/<span[^>]*>/g, '') // Entferne Span-Tags komplett
        .replace(/<\/span>/g, ''); // Entferne schließende Span-Tags
    
    // ✅ SCHRITT 6: Überflüssige Style-Attribute entfernen die Fonts überschreiben
    fixed = fixed.replace(
        /style="[^"]*font-family[^"]*"/gi,
        ''
    );
    
    // ✅ SCHRITT 7: Leere Style-Attribute entfernen
    fixed = fixed.replace(/style=""/g, '');
    
    return fixed;
};

/**
 * ✅ FIRESTORE RICH TEXT WATCHER - Überwacht DOM-Änderungen
 * Startet einen MutationObserver für dynamisch geladene Inhalte
 */
export const startLivePageRichTextWatcher = () => {
    const isLivePage = document.querySelector('.page_public') !== null;
    if (!isLivePage) return;
    
    console.log('🔧 Rich Text Watcher: Starte DOM-Überwachung für Firestore-Inhalte');
    
    const observer = new MutationObserver((mutations) => {
        let shouldFix = false;
        
        mutations.forEach((mutation) => {
            if (mutation.type === 'childList' || mutation.type === 'characterData') {
                // Prüfe ob neue Elemente mit Rich Text hinzugefügt wurden
                const addedNodes = Array.from(mutation.addedNodes);
                addedNodes.forEach(node => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const textContent = node.textContent || node.innerHTML;
                        if (textContent && isDisplayingHtmlAsText(textContent)) {
                            shouldFix = true;
                        }
                    }
                });
            }
        });
        
        if (shouldFix) {
            console.log('🔧 Rich Text Watcher: DOM-Änderung erkannt, führe Korrektur aus');
            setTimeout(() => initLivePageRichTextFixer(), 100);
        }
    });
    
    // Überwache das gesamte page_public Element
    const pagePublic = document.querySelector('.page_public');
    if (pagePublic) {
        observer.observe(pagePublic, {
            childList: true,
            subtree: true,
            characterData: true
        });
        
        console.log('✅ Rich Text Watcher: DOM-Überwachung aktiv');
    }
};

